/**
 * 图片图层预览工具：根据当前事件之前的显示/移动/消失事件，推导图层上的图片路径。
 */
import EventID from '@/model/EventClass/EventID'
import type EventBase from '@/model/EventClass/EventBase'

/** 当前事件之前仍有效的图片图层。 */
export type InheritedImageLayer = {
  layerId: number
  path: string
}

/** 从事件 Parameter[0] 读取图层编号。 */
function readLayerId(event: EventBase): number {
  return Number.parseInt(String(event.Parameter?.[0] ?? ''), 10)
}

/** 从事件 Parameter[1] 读取图片路径。 */
function readPicPath(event: EventBase): string {
  return String(event.Parameter?.[1] ?? '').trim()
}

/**
 * 向上查找指定图层在当前事件之前最后一次显示的图片路径。
 * @param events 当前剧情事件列表
 * @param selectedIndex 当前事件索引
 * @param layerId 目标图层编号
 */
export function findInheritedLayerPicturePath(
  events: EventBase[],
  selectedIndex: number | null | undefined,
  layerId: number,
): string {
  if (selectedIndex == null || selectedIndex < 0 || layerId <= 0) return ''
  for (let index = selectedIndex - 1; index >= 0; index -= 1) {
    const event = events[index]
    if (!event) continue
    if (event.Code === EventID.DisposePic) {
      const disposeLayerId = Number.parseInt(String(event.Parameter?.[0] ?? ''), 10)
      if (disposeLayerId === layerId) return ''
      continue
    }
    if (event.Code === EventID.PicShow || event.Code === EventID.MovePic) {
      const picLayerId = Number.parseInt(String(event.Parameter?.[0] ?? ''), 10)
      if (picLayerId !== layerId) continue
      const path = String(event.Parameter?.[1] ?? '').trim()
      if (path) return path
    }
  }
  return ''
}

/**
 * 收集当前事件之前仍有图片路径的图层。
 * @param events 当前剧情事件列表
 * @param selectedIndex 当前事件索引
 * @returns 按图层编号升序排列的可选图片图层
 */
export function collectInheritedImageLayers(
  events: EventBase[],
  selectedIndex: number | null | undefined,
): InheritedImageLayer[] {
  if (selectedIndex == null || selectedIndex < 0) return []
  const layers = new Map<number, string>()
  for (let index = 0; index < selectedIndex; index += 1) {
    const event = events[index]
    if (!event) continue
    const layerId = readLayerId(event)
    if (!Number.isFinite(layerId) || layerId <= 0) continue
    if (event.Code === EventID.DisposePic) {
      layers.delete(layerId)
      continue
    }
    if (event.Code !== EventID.PicShow && event.Code !== EventID.MovePic) continue
    const path = readPicPath(event)
    if (path) {
      layers.set(layerId, path)
    }
  }
  return [...layers.entries()]
    .map(([layerId, path]) => ({ layerId, path }))
    .sort((a, b) => a.layerId - b.layerId)
}

/**
 * 图片资源选择器在未匹配到已选路径时的默认文件夹。
 * @param picId 图层编号：1/2/23 为背景类，其它为立绘类。
 */
export function getPicResourceFallbackFolder(picId: number): string {
  const id = Math.round(Number(picId) || 0)
  if (id === 1 || id === 2 || id === 23) return '背景'
  return '立绘'
}
